/**
 * Labstep
 *
 * @module components/Academic/Wizard/Payment
 * @desc Show modal with wizard to upgrade academic account
 */

import { OrganizationPlanManagerPriceContainer as OrganizationPlanManagerPrice } from 'labstep-web/components/OrganizationPlan/Manager/Price';
import { defaultCurrency } from 'labstep-web/constants/organization-plan-price';
import { StripeContainer } from 'labstep-web/containers/Stripe';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { ModalWizard } from 'labstep-web/core/Modal/Wizard';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import type { OrganizationPlanPrice } from 'labstep-web/models/organization-plan-price.model';
import { User } from 'labstep-web/models/user.model';
import type { CurrencyISO } from 'labstep-web/services/currency.service';
import { intercomService } from 'labstep-web/services/intercom.service';
import { navigation } from 'labstep-web/services/navigation';
import { windowService } from 'labstep-web/services/window.service';
import type React from 'react';
import { useState } from 'react';
import {
  AcademicWizardPaymentSeats,
  MaxAcademicWizardPaymentSeats,
} from './Seats';
import type {
  AcademicWizardPaymentContainerProps,
  AcademicWizardPaymentProps,
} from './types';

export const ROUTE_ID = 'academic-upgrade';

export const AcademicWizardPayment: React.FC<
  AcademicWizardPaymentProps
> = ({
  actionComponentProps,
  userCount,
  createStripeCheckoutSession,
  ...rest
}) => {
  const [activePlanPrice, setActivePlanPrice] =
    useState<OrganizationPlanPrice | null>(null);
  const [priceCurrency, setPriceCurrency] =
    useState<CurrencyISO>(defaultCurrency);

  const [maxSeats, setMaxSeats] = useState(userCount);

  return (
    <ModalWizard
      mode="fullscreen"
      routeId={ROUTE_ID}
      title="Upgrade"
      trigger={({ toggleModal }) => (
        <ActionComponent
          type="button"
          dataTestId="academic-plus-upgrade"
          {...actionComponentProps}
          onClick={() => toggleModal()}
        />
      )}
      steps={['Select a plan', 'Select seats']}
      {...rest}
    >
      <ModalWizard.Step index={0}>
        <OrganizationPlanManagerPrice
          priceCurrency={priceCurrency}
          setPriceCurrency={setPriceCurrency}
          activePlanPrice={activePlanPrice}
          setActivePlanPrice={setActivePlanPrice}
          academic
        />
      </ModalWizard.Step>
      <ModalWizard.Step
        index={1}
        hideHeader
        continueLabel="Setup Payment"
        continueText="Continuing will take you off-site to Stripe’s payment gateway"
        isContinueButtonDisabled={
          maxSeats > MaxAcademicWizardPaymentSeats
        }
        onContinue={() => {
          if (!activePlanPrice) {
            return;
          }
          intercomService.trackEvent(
            'checkout-academic-plus-upgrade',
            {},
          );
          createStripeCheckoutSession(
            {
              price_id: activePlanPrice?.id,
              currency: priceCurrency,
              quantity: maxSeats,
              success_url: navigation.get('root', {}, true),
              cancel_url: window.location.href,
            },
            {
              onSuccess: ({ response }) => {
                if (response?.url) {
                  windowService.setLocation(response.url as string);
                }
              },
            },
          );
        }}
      >
        {activePlanPrice && (
          <AcademicWizardPaymentSeats
            userCount={userCount}
            priceCurrency={priceCurrency}
            activePlanPrice={activePlanPrice}
            maxSeats={maxSeats}
            setMaxSeats={setMaxSeats}
          />
        )}
      </ModalWizard.Step>
    </ModalWizard>
  );
};

export const AcademicWizardPaymentContainer: React.FC<
  AcademicWizardPaymentContainerProps
> = ({ actionComponentProps }) => {
  return (
    <ReadOnMountHOC
      type="count"
      entityName={User.entityName}
      params={{
        count: 1,
        is_owner_group_member: 1,
      }}
      children={({ total }) => {
        if (total <= 0) {
          return null;
        }
        if (total < MaxAcademicWizardPaymentSeats) {
          return (
            <StripeContainer>
              {({ createStripeCheckoutSession }) => (
                <AcademicWizardPayment
                  actionComponentProps={actionComponentProps}
                  userCount={total}
                  createStripeCheckoutSession={
                    createStripeCheckoutSession
                  }
                />
              )}
            </StripeContainer>
          );
        }
        return (
          <div className="academic-upgrade">
            <ActionComponent
              type="button"
              dataTestId="academic-plus-intercom"
              {...actionComponentProps}
            />
          </div>
        );
      }}
    />
  );
};
